import { useState } from 'react';
import useFetch from '../../../../hooks/useFetch';
import { ServiceEvents } from '../../shared/interfaces';
import { buildEndpointIntranetCompany } from '../../EndPoints';
import { CompanyUser, User, UserPasswordDataForm } from '../../../models/User';

type Credentials = {
    username: string;
    password: string;
};

const useAuthCompanyService = () => {
    const { doGet, doPost, doPut } = useFetch();

    const [fetchingLoginAuth, setFetchingLoginAuth] = useState(false);
    const [fetchingLogoutAuth, setFetchingLogoutAuth] = useState(false);
    const [fetchingValidateTokenAuth, setFetchingValidateTokenAuth] = useState(false);
    const [fetchingGetRoles, setFetchingGetRoles] = useState(false);
    const [fetchingSendCodeRecoveryPassword, setFetchingSendCodeRecoveryPassword] = useState(false);
    const [fetchingChangePasswordByCode, setFetchingChangePasswordByCode] = useState(false);
    const [fetchingGetProfile, setFetchingGetProfile] = useState(false);
    const [fetchingUpdateProfile, setFetchingUpdateProfile] = useState(false);
    const [fetchingUpdateProfilePassword, setFetchingUpdateProfilePassword] = useState(false);

    const loginAuth = (credentials: Credentials, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            headers: {
                Authorization: ''
            },
            url: `${buildEndpointIntranetCompany(`auths/login`)}`,
            body: credentials,
            type: 'company',
            setFetching: setFetchingLoginAuth
        });
    };

    const logoutAuth = (events: ServiceEvents = {}) => {
        doPost({
            ...events,
            type: 'company',
            url: `${buildEndpointIntranetCompany(`auths/logout`)}`,
            setFetching: setFetchingLogoutAuth
        });
    };

    const validateTokenAuth = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            type: 'company',
            url: `${buildEndpointIntranetCompany(`auths/validate-token`)}`,
            setFetching: setFetchingValidateTokenAuth
        });
    };

    const getRoles = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            type: 'company',
            url: `${buildEndpointIntranetCompany(`auths/get-roles`)}`,
            setFetching: setFetchingGetRoles
        });
    };

    const sendCodeRecoveryPassword = (email: string, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            type: 'company',
            url: `${buildEndpointIntranetCompany(`auths/send-code-recovery-password`)}`,
            headers: {
                Authorization: ''
            },
            body: { email: email },
            setFetching: setFetchingSendCodeRecoveryPassword
        });
    };

    const changePasswordByCode = (
        code: string,
        password: string,
        repeat_password: string,
        events: ServiceEvents = {}
    ) => {
        doPost({
            ...events,
            url: `${buildEndpointIntranetCompany(`auths/change-password-by-code`)}`,
            headers: {
                Authorization: ''
            },
            body: {
                code_recovery_password: code,
                password: password,
                repeat_password: repeat_password
            },
            setFetching: setFetchingChangePasswordByCode
        });
    };

    const getProfile = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: `${buildEndpointIntranetCompany(`auths/get-profile`)}`,
            setFetching: setFetchingGetProfile
        });
    };

    const updateProfile = (userProfile: User, events: ServiceEvents = {}) => {
        doPut({
            ...events,
            url: `${buildEndpointIntranetCompany(`auths/update-profile`)}`,
            body: userProfile,
            setFetching: setFetchingUpdateProfile
        });
    };

    const updateProfilePassword = (
        userProfile: UserPasswordDataForm,
        events: ServiceEvents = {}
    ) => {
        doPut({
            ...events,
            url: `${buildEndpointIntranetCompany(`auths/update-profile-password`)}`,
            body: userProfile,
            setFetching: setFetchingUpdateProfilePassword
        });
    };

    const registerCompanyUser = (companyUserProfile: CompanyUser, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: `${buildEndpointIntranetCompany(`auths/register-company-user`)}`,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            requestType: 'json',
            body: companyUserProfile,
            setFetching: setFetchingUpdateProfile
        });
    };

    return {
        loginAuth,
        logoutAuth,
        validateTokenAuth,
        getRoles,
        sendCodeRecoveryPassword,
        changePasswordByCode,
        registerCompanyUser,
        fetchingGetProfile,
        fetchingUpdateProfile,
        fetchingChangePasswordByCode,
        fetchingSendCodeRecoveryPassword,
        fetchingLoginAuth,
        fetchingGetRoles,
        fetchingLogoutAuth,
        fetchingValidateTokenAuth,
        fetchingUpdateProfilePassword,
        getProfile,
        updateProfile,
        updateProfilePassword
    };
};

export default useAuthCompanyService;
